import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OmCard, OmHead, OmKpi, OmTable, Row, Td, Pill, GOLD, MUTED, NAVY, FIELD } from "@/components/om/OmBits";
import { useMeta } from "@/lib/useMeta";

const BLANK = { fault_name: "", asset_category: "Electrical", fault_description: "", why_1: "", why_2: "", why_3: "", why_4: "", why_5: "", recommended_action: "" };
const CATEGORIES = ["Electrical", "Mechanical", "Solar", "Wind", "Thermal", "HVAC", "Instrumentation", "Civil", "Water Treatment", "Other"];
const clip = (v, n = 60) => (v && v.length > n ? `${v.slice(0, n)}…` : v || "—");

export default function AdminRcaTemplates() {
  useMeta({ title: "RCA Template Manager | Plan-Sarthi", description: "Fault taxonomy for the 5-Why engine.", path: "/admin/rca-templates" });
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/rca-templates").then(({ data }) => setData(data))
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => (data?.templates || []).filter((t) =>
    (!q || `${t.fault_name} ${t.fault_description}`.toLowerCase().includes(q.toLowerCase())) &&
    (!cat || t.asset_category === cat)), [data, q, cat]);

  const submit = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/admin/rca-templates/${form.id}`, form);
      else await api.post("/admin/rca-templates", form);
      toast.success(form.id ? "Template updated" : "Template created — the engine can use it immediately");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (t) => {
    try { await api.delete(`/admin/rca-templates/${t.id}`); toast.success(`${t.fault_name} deleted`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid="admin-rca-templates-page">
      <div className="max-w-[1500px]">
        <OmHead title="RCA Template Manager" subtitle="The fault taxonomy behind the automated 5-Why engine. New templates are live the moment they are saved.">
          <Button onClick={() => setForm({ ...BLANK })} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8 }} data-testid="admin-rca-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add new template
          </Button>
        </OmHead>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          <OmKpi label="Templates" value={data?.count ?? "—"} testid="admin-rca-kpi-count" />
          <OmKpi label="Asset categories covered" value={data?.categories.length ?? "—"} />
          <OmKpi label="Seeded by Plan-Sarthi" value={(data?.templates || []).filter((t) => t.seeded).length} />
        </div>

        <OmCard className="mb-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-3" style={{ color: MUTED }} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fault name or description" className="pl-8 w-[260px]" style={FIELD} data-testid="admin-rca-search" />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="admin-rca-category-filter">
              <option value="">All categories</option>
              {(data?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Pill tone="gold" testid="admin-rca-count">{rows.length} shown</Pill>
          </div>
        </OmCard>

        <OmCard testid="admin-rca-table-card">
          <OmTable head={["Fault name", "Category", "Why 1", "Why 3", "Why 5", "Recommended action", "Updated", ""]} testid="admin-rca-table">
            {rows.map((t, i) => (
              <Row key={t.id} i={i} testid={`admin-rca-row-${t.fault_name.replace(/\s+/g, "-").toLowerCase()}`}>
                <Td><span className="text-white font-medium">{t.fault_name}</span></Td>
                <Td><Pill tone="gold">{t.asset_category}</Pill></Td>
                <Td><span style={{ color: MUTED }}>{clip(t.why_1, 44)}</span></Td>
                <Td><span style={{ color: MUTED }}>{clip(t.why_3, 44)}</span></Td>
                <Td><span style={{ color: MUTED }}>{clip(t.why_5, 44)}</span></Td>
                <Td><span style={{ color: MUTED }}>{clip(t.recommended_action, 48)}</span></Td>
                <Td>{(t.updated_at || "").slice(0, 10)}</Td>
                <Td>
                  <div className="flex gap-2">
                    <button onClick={() => setForm({ ...t })} title="Edit" data-testid={`admin-rca-edit-${t.id.slice(0, 6)}`}><Pencil className="w-4 h-4" style={{ color: GOLD }} /></button>
                    <button onClick={() => remove(t)} title="Delete" data-testid={`admin-rca-delete-${t.id.slice(0, 6)}`}><Trash2 className="w-4 h-4" style={{ color: "#EF4444" }} /></button>
                  </div>
                </Td>
              </Row>
            ))}
          </OmTable>
        </OmCard>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }} data-testid="admin-rca-dialog">
          <DialogHeader><DialogTitle className="text-white">{form?.id ? "Edit template" : "New fault template"}</DialogTitle></DialogHeader>
          {form ? (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Fault name</Label>
                  <Input value={form.fault_name} onChange={(e) => setForm({ ...form, fault_name: e.target.value })} style={FIELD} data-testid="admin-rca-fault-name" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Asset category</Label>
                  <select value={form.asset_category} onChange={(e) => setForm({ ...form, asset_category: e.target.value })}
                    className="w-full h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid="admin-rca-asset-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Fault description (used for similarity matching)</Label>
                <Textarea rows={2} value={form.fault_description} onChange={(e) => setForm({ ...form, fault_description: e.target.value })} style={FIELD} data-testid="admin-rca-fault-description" />
              </div>
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n}>
                  <Label className="text-[11px] uppercase" style={{ color: GOLD }}>Why {n}</Label>
                  <Textarea rows={2} value={form[`why_${n}`]} onChange={(e) => setForm({ ...form, [`why_${n}`]: e.target.value })} style={FIELD} data-testid={`admin-rca-why-${n}`} />
                </div>
              ))}
              <div>
                <Label className="text-[11px] uppercase" style={{ color: MUTED }}>Recommended action</Label>
                <Textarea rows={3} value={form.recommended_action} onChange={(e) => setForm({ ...form, recommended_action: e.target.value })} style={FIELD} data-testid="admin-rca-recommended-action" />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={submit} disabled={busy || !form?.fault_name || !form?.fault_description} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="admin-rca-save-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} {form?.id ? "Save changes" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
